import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useEvent } from '../hooks/useEvent';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { ShareLinkButton } from '../components/ShareLinkButton';
import { LogoUpload } from '../components/LogoUpload';
import { ParGrid } from '../components/ParGrid';
import { eventsApi } from '../api/events';
import { ApiError } from '../api/client';
import type { HoleFlag } from '../api/types';

type AddPlayerPrompt = { teamId: string | null };

export function EventLobby() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, error, isLoading, refetch } = useEvent(id);
  const { actor } = useAuth();
  const qc = useQueryClient();
  const [newTeam, setNewTeam] = useState('');
  const [memberNewTeam, setMemberNewTeam] = useState('');
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null);
  const [editingTeamName, setEditingTeamName] = useState('');
  const [editingEvent, setEditingEvent] = useState(false);
  const [editName, setEditName] = useState('');
  const [editLogoUrl, setEditLogoUrl] = useState<string | null>(null);
  const [editAllowGuestTeams, setEditAllowGuestTeams] = useState(false);
  const [editingHoles, setEditingHoles] = useState(false);
  const [editPars, setEditPars] = useState<number[]>([]);
  const [editFlags, setEditFlags] = useState<HoleFlag[]>([]);
  const [editingPlayerId, setEditingPlayerId] = useState<string | null>(null);
  const [editingPlayerName, setEditingPlayerName] = useState('');
  const [addPlayerPrompt, setAddPlayerPrompt] = useState<AddPlayerPrompt | null>(null);
  const [addPlayerName, setAddPlayerName] = useState('');
  const [addPlayerHandicap, setAddPlayerHandicap] = useState('');
  const [deleteEventOpen, setDeleteEventOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (isLoading) return <div className="container muted">Loading…</div>;
  if (error || !data) return <div className="container error">Failed to load event</div>;

  const { event, holes, participants, teams } = data;
  const isAdmin = actor?.kind === 'user' && event.admin_user_id === actor.user.id;
  const myParticipant = participants.find(
    (p) =>
      (actor?.kind === 'user' && p.userId === actor.user.id) ||
      (actor?.kind === 'guest' && p.guestId === actor.guest.id),
  );
  const userHasTeam = !!myParticipant?.teamId;
  const canMemberCreateTeam =
    !!myParticipant &&
    !isAdmin &&
    event.format === 'best_ball_4some' &&
    event.allow_guest_teams &&
    !userHasTeam &&
    event.status !== 'final';

  const handle = async <T,>(fn: () => Promise<T>) => {
    setErr(null);
    setBusy(true);
    try {
      await fn();
      await refetch();
      qc.invalidateQueries({ queryKey: ['leaderboard', id] });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const startEventEdit = () => {
    setEditName(event.name);
    setEditLogoUrl(event.logo_url);
    setEditAllowGuestTeams(event.allow_guest_teams);
    setEditingEvent(true);
  };

  const saveEventEdit = async () => {
    const trimmed = editName.trim();
    if (!trimmed) {
      setErr('Event name is required');
      return;
    }
    await handle(() =>
      eventsApi.patch(event.id, {
        name: trimmed,
        logoUrl: editLogoUrl,
        ...(event.format === 'best_ball_4some' ? { allowGuestTeams: editAllowGuestTeams } : {}),
      }),
    );
    setEditingEvent(false);
  };

  const startHolesEdit = () => {
    setEditPars(holes.map((h) => h.par));
    setEditFlags(holes.map((h) => ({ holeNumber: h.holeNumber, isKp: h.isKp, isLd: h.isLd, isHio: h.isHio })));
    setEditingHoles(true);
  };

  const saveHolesEdit = async () => {
    await handle(() =>
      eventsApi.patch(event.id, { pars: editPars, holeFlags: editFlags }),
    );
    setEditingHoles(false);
  };

  const startTeamEdit = (teamId: string, currentName: string) => {
    setEditingTeamId(teamId);
    setEditingTeamName(currentName);
  };

  const saveTeamEdit = async (teamId: string) => {
    const trimmed = editingTeamName.trim();
    if (!trimmed) {
      setEditingTeamId(null);
      return;
    }
    await handle(() => eventsApi.patchTeam(event.id, teamId, trimmed));
    setEditingTeamId(null);
  };

  const openAddPlayer = (teamId: string | null) => {
    setAddPlayerName('');
    setAddPlayerHandicap('');
    setAddPlayerPrompt({ teamId });
  };

  const submitAddPlayer = async () => {
    if (!addPlayerPrompt) return;
    const name = addPlayerName.trim();
    if (!name) {
      setErr('Player name is required');
      return;
    }
    const hcap = addPlayerHandicap.trim() ? Number(addPlayerHandicap) : undefined;
    await handle(() =>
      eventsApi.createPlayer(event.id, {
        displayName: name,
        teamId: addPlayerPrompt.teamId,
        handicap: hcap,
      }),
    );
    setAddPlayerPrompt(null);
  };

  const startPlayerEdit = (pid: string, currentName: string) => {
    setEditingPlayerId(pid);
    setEditingPlayerName(currentName);
  };

  const savePlayerEdit = async (pid: string) => {
    const trimmed = editingPlayerName.trim();
    if (!trimmed) {
      setEditingPlayerId(null);
      return;
    }
    await handle(() => eventsApi.patchParticipant(event.id, pid, { displayName: trimmed }));
    setEditingPlayerId(null);
  };

  const deletePlayer = async (pid: string, name: string) => {
    if (!confirm(`Remove placeholder player "${name}"?`)) return;
    await handle(() => eventsApi.deletePlayer(event.id, pid));
  };

  const confirmDeleteEvent = async () => {
    setErr(null);
    setBusy(true);
    try {
      await eventsApi.remove(event.id);
      qc.invalidateQueries({ queryKey: ['events', 'mine'] });
      setDeleteEventOpen(false);
      navigate('/events');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not delete event');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container stack">
      <div className="card stack">
        <div className="row" style={{ gap: 12 }}>
          {event.logo_url && (
            <img src={event.logo_url} alt="" width={56} height={56} style={{ borderRadius: 8, objectFit: 'cover' }} />
          )}
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0 }}>{event.name}</h1>
            <div className="muted" style={{ fontSize: 13 }}>
              {event.format === 'best_ball_4some' ? 'Best Ball' : 'Stroke Play'} · {event.num_holes} holes · <span className="badge">{event.status}</span>
            </div>
          </div>
          {isAdmin && event.status !== 'final' && !editingEvent && (
            <div className="row" style={{ gap: 8 }}>
              <Button variant="secondary" onClick={startEventEdit}>Edit</Button>
              <Button variant="danger" disabled={busy} onClick={() => setDeleteEventOpen(true)}>
                Delete
              </Button>
            </div>
          )}
        </div>
        {isAdmin && <ShareLinkButton shortCode={event.short_code} />}
      </div>

      {err && <div className="error">{err}</div>}

      {isAdmin && editingEvent && event.status !== 'final' && (
        <div className="card stack">
          <h3>Edit event</h3>
          <div>
            <label htmlFor="edit-name">Event name</label>
            <input
              id="edit-name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              maxLength={120}
            />
          </div>
          <div>
            <label>Logo</label>
            <LogoUpload value={editLogoUrl} onChange={setEditLogoUrl} />
          </div>
          {event.format === 'best_ball_4some' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
              <input
                type="checkbox"
                checked={editAllowGuestTeams}
                onChange={(e) => setEditAllowGuestTeams(e.target.checked)}
              />
              Allow guests to create teams
            </label>
          )}
          <div className="row" style={{ gap: 8 }}>
            <Button onClick={saveEventEdit} disabled={busy}>Save changes</Button>
            <Button variant="secondary" onClick={() => setEditingEvent(false)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}

      {isAdmin && event.status === 'draft' && !editingEvent && (
        <div className="card stack">
          <h3>Get the event ready</h3>
          <p className="muted">Set up teams (best ball) and confirm pars. Then start the event so players can submit scores.</p>
          <Button variant="accent" block disabled={busy} onClick={() => handle(() => eventsApi.start(event.id))}>
            Start event
          </Button>
        </div>
      )}

      {isAdmin && event.status === 'active' && (
        <Button variant="accent" block disabled={busy} onClick={() => {
          if (confirm('Finalize event? This locks all scoring permanently for everyone, including you.')) {
            handle(() => eventsApi.finalize(event.id));
          }
        }}>
          Finalize event
        </Button>
      )}

      <div className="card stack">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>Holes & pars</h3>
          {isAdmin && event.status === 'draft' && !editingHoles && (
            <Button variant="secondary" onClick={startHolesEdit}>Edit pars & KP/LD/HIO</Button>
          )}
        </div>
        {!editingHoles && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {holes.map((h) => (
              <span key={h.holeNumber} className="badge" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                H{h.holeNumber}: par {h.par}
                {h.isKp && <span className="flag-chip flag-chip-kp" style={{ cursor: 'default' }}>KP</span>}
                {h.isLd && <span className="flag-chip flag-chip-ld" style={{ cursor: 'default' }}>LD</span>}
                {h.isHio && <span className="flag-chip flag-chip-hio" style={{ cursor: 'default' }}>HIO</span>}
              </span>
            ))}
          </div>
        )}
        {editingHoles && isAdmin && event.status === 'draft' && (
          <div className="stack">
            <p className="muted" style={{ fontSize: 12, margin: 0 }}>
              KP, LD and the HIO prize hole can only be edited while the event is in draft. They lock once you start the event.
            </p>
            <ParGrid
              pars={editPars}
              flags={editFlags}
              onChange={setEditPars}
              onFlagsChange={setEditFlags}
              showFlags
            />
            <div className="row" style={{ gap: 8 }}>
              <Button onClick={saveHolesEdit} disabled={busy}>Save holes</Button>
              <Button variant="secondary" onClick={() => setEditingHoles(false)} disabled={busy}>Cancel</Button>
            </div>
          </div>
        )}
      </div>

      {event.format === 'best_ball_4some' && (
        <div className="card stack">
          <h3>Teams</h3>
          {teams.length === 0 && <p className="muted">No teams yet.</p>}
          <div className="team-grid">
            {teams.map((t) => {
              const members = participants.filter((p) => p.teamId === t.id);
              const onMyTeam = myParticipant?.teamId === t.id;
              const isEditingThis = editingTeamId === t.id;
              return (
                <div key={t.id} className="card" style={{ background: onMyTeam ? 'var(--color-green-100)' : undefined }}>
                  <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
                    {isEditingThis ? (
                      <input
                        autoFocus
                        value={editingTeamName}
                        onChange={(e) => setEditingTeamName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveTeamEdit(t.id);
                          if (e.key === 'Escape') setEditingTeamId(null);
                        }}
                        onBlur={() => saveTeamEdit(t.id)}
                        maxLength={60}
                        style={{ flex: 1 }}
                      />
                    ) : (
                      <strong>{t.name}</strong>
                    )}
                    <span className="muted" style={{ fontSize: 12 }}>{members.length}/4</span>
                  </div>
                  <ul style={{ margin: '8px 0', paddingLeft: 18 }}>
                    {members.map((m) => (
                      <li key={m.id}>
                        <PlayerRow
                          participant={m}
                          isAdmin={isAdmin}
                          isMe={m.id === myParticipant?.id}
                          isEditing={editingPlayerId === m.id}
                          editingName={editingPlayerName}
                          onEditingNameChange={setEditingPlayerName}
                          onStartEdit={() => startPlayerEdit(m.id, m.displayName)}
                          onSaveEdit={() => savePlayerEdit(m.id)}
                          onCancelEdit={() => setEditingPlayerId(null)}
                          onDelete={() => deletePlayer(m.id, m.displayName)}
                          busy={busy}
                        />
                      </li>
                    ))}
                  </ul>
                  <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                    {isAdmin && event.status !== 'final' && members.length < 4 && (
                      <Button variant="secondary" disabled={busy} onClick={() => openAddPlayer(t.id)}>
                        + Add player
                      </Button>
                    )}
                    {!onMyTeam && myParticipant && !userHasTeam && (
                      <Button variant="secondary" disabled={busy || members.length >= 4 || event.status === 'final'} onClick={() => handle(() => eventsApi.joinTeam(event.id, t.id))}>
                        Join team
                      </Button>
                    )}
                    {onMyTeam && (
                      <Button variant="secondary" disabled={busy || event.status === 'final'} onClick={() => handle(() => eventsApi.leaveTeam(event.id, t.id))}>
                        Leave team
                      </Button>
                    )}
                    {isAdmin && !isEditingThis && event.status !== 'final' && (
                      <Button variant="secondary" disabled={busy} onClick={() => startTeamEdit(t.id, t.name)}>
                        Rename
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="danger" disabled={busy || event.status === 'final'} onClick={() => {
                        if (confirm(`Delete team "${t.name}"?`)) handle(() => eventsApi.deleteTeam(event.id, t.id));
                      }}>
                        Delete
                      </Button>
                    )}
                  </div>
                  {isAdmin && event.status === 'active' && (
                    <Link
                      to={`/e/${event.id}/score?as=${t.id}`}
                      style={{ display: 'block', marginTop: 8 }}
                    >
                      <Button block>Enter score</Button>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
          {isAdmin && event.status !== 'final' && (
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                if (!newTeam.trim()) return;
                const name = newTeam.trim();
                setNewTeam('');
                handle(() => eventsApi.createTeam(event.id, name));
              }}
              style={{ gap: 8 }}
            >
              <input value={newTeam} onChange={(e) => setNewTeam(e.target.value)} placeholder="Team name" maxLength={60} />
              <Button type="submit" disabled={busy}>+ Add team</Button>
            </form>
          )}
          {canMemberCreateTeam && (
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                if (!memberNewTeam.trim()) return;
                const name = memberNewTeam.trim();
                setMemberNewTeam('');
                handle(() => eventsApi.createTeam(event.id, name));
              }}
              style={{ gap: 8 }}
            >
              <input
                value={memberNewTeam}
                onChange={(e) => setMemberNewTeam(e.target.value)}
                placeholder="Create a new team"
                maxLength={60}
              />
              <Button type="submit" disabled={busy}>+ Create team</Button>
            </form>
          )}
        </div>
      )}

      <div className="card stack">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>Players ({participants.length})</h3>
          {isAdmin && event.status !== 'final' && (
            <Button variant="secondary" disabled={busy} onClick={() => openAddPlayer(null)}>
              + Add player
            </Button>
          )}
        </div>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {participants.map((p) => (
            <li key={p.id}>
              <PlayerRow
                participant={p}
                isAdmin={isAdmin}
                isMe={p.id === myParticipant?.id}
                isEditing={editingPlayerId === p.id}
                editingName={editingPlayerName}
                onEditingNameChange={setEditingPlayerName}
                onStartEdit={() => startPlayerEdit(p.id, p.displayName)}
                onSaveEdit={() => savePlayerEdit(p.id)}
                onCancelEdit={() => setEditingPlayerId(null)}
                onDelete={() => deletePlayer(p.id, p.displayName)}
                busy={busy}
              />
            </li>
          ))}
        </ul>
      </div>

      <Modal
        open={deleteEventOpen}
        title="Delete this event?"
        body={
          <p style={{ margin: 0 }}>
            Are you sure you want to delete <strong>{event.name}</strong>? It will be hidden from
            everyone and won't appear in event lists. Scores and history are preserved server-side
            but the event becomes inaccessible. This cannot be undone from the app.
          </p>
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => setDeleteEventOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDeleteEvent} disabled={busy}>
              {busy ? 'Deleting…' : 'Delete event'}
            </Button>
          </>
        }
        onClose={() => setDeleteEventOpen(false)}
      />

      <Modal
        open={!!addPlayerPrompt}
        title="Add player"
        body={
          <div className="stack">
            <div>
              <label htmlFor="add-player-name">Player name</label>
              <input
                id="add-player-name"
                autoFocus
                value={addPlayerName}
                onChange={(e) => setAddPlayerName(e.target.value)}
                maxLength={60}
                placeholder="e.g. Juan Pérez"
              />
            </div>
            {event.format === 'best_ball_4some' && (
              <div>
                <label htmlFor="add-player-team">Team</label>
                <select
                  id="add-player-team"
                  value={addPlayerPrompt?.teamId ?? ''}
                  onChange={(e) =>
                    setAddPlayerPrompt((prev) =>
                      prev ? { ...prev, teamId: e.target.value || null } : prev,
                    )
                  }
                >
                  <option value="">No team yet</option>
                  {teams.map((t) => {
                    const memberCount = participants.filter((p) => p.teamId === t.id).length;
                    const full = memberCount >= 4;
                    return (
                      <option key={t.id} value={t.id} disabled={full}>
                        {t.name} ({memberCount}/4){full ? ' — full' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
            <div>
              <label htmlFor="add-player-hcap">Handicap (optional)</label>
              <input
                id="add-player-hcap"
                type="number"
                min={0}
                max={54}
                step="0.1"
                value={addPlayerHandicap}
                onChange={(e) => setAddPlayerHandicap(e.target.value)}
                placeholder="e.g. 14.2"
              />
            </div>
            <p className="muted" style={{ fontSize: 12, margin: 0 }}>
              This creates a placeholder. Share the event link and the player picks their name to claim the slot.
            </p>
          </div>
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => setAddPlayerPrompt(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submitAddPlayer} disabled={busy}>Add player</Button>
          </>
        }
        onClose={() => setAddPlayerPrompt(null)}
      />

      <div className="row" style={{ gap: 8, justifyContent: 'center' }}>
        {event.status === 'active' && (
          <Link to={`/e/${event.id}/score`}><Button>Enter scores</Button></Link>
        )}
        <Link to={`/e/${event.id}/leaderboard`}><Button variant="secondary">View leaderboard</Button></Link>
      </div>
    </div>
  );
}

type PlayerRowProps = {
  participant: {
    id: string;
    displayName: string;
    placeholderName: string | null;
    handicap: number | null;
    isAdmin: boolean;
  };
  isAdmin: boolean;
  isMe: boolean;
  isEditing: boolean;
  editingName: string;
  onEditingNameChange: (v: string) => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  busy: boolean;
};

function PlayerRow({
  participant: p,
  isAdmin,
  isMe,
  isEditing,
  editingName,
  onEditingNameChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  busy,
}: PlayerRowProps) {
  const isPlaceholder = p.placeholderName !== null;
  const canEdit = isAdmin || isMe;
  if (isEditing) {
    return (
      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        <input
          autoFocus
          value={editingName}
          onChange={(e) => onEditingNameChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSaveEdit();
            if (e.key === 'Escape') onCancelEdit();
          }}
          onBlur={onSaveEdit}
          maxLength={60}
          style={{ width: 'auto', display: 'inline-block' }}
        />
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontStyle: isPlaceholder ? 'italic' : undefined, color: isPlaceholder ? 'var(--text-muted)' : undefined }}>
        {p.displayName}
      </span>
      {p.isAdmin && <span className="badge">admin</span>}
      {isPlaceholder && <span className="badge">pending</span>}
      {p.handicap != null && <span className="muted" style={{ fontSize: 12 }}>hcp {p.handicap}</span>}
      {canEdit && (
        <button
          type="button"
          aria-label="Rename player"
          disabled={busy}
          onClick={onStartEdit}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-primary)', padding: '0 4px', fontSize: 14 }}
        >
          ✎
        </button>
      )}
      {isAdmin && isPlaceholder && (
        <button
          type="button"
          aria-label="Remove placeholder player"
          disabled={busy}
          onClick={onDelete}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', padding: '0 4px', fontSize: 14 }}
        >
          ×
        </button>
      )}
    </span>
  );
}
